/**
 * New-deploy detection and the reload that applies it.
 *
 * Every build stamps itself (`__APP_VERSION__`) and ships the same stamp in
 * `/version.json`. A tab that has been open across a deploy keeps running the
 * old bundle; `checkForUpdate` notices the server's stamp is newer, and
 * `applyUpdate` is what the "refresh" button in AppUpdatePopup runs.
 */

const VERSION_URL = `${import.meta.env.BASE_URL}version.json`;

/** Upper bound on each service-worker step before reloading regardless. */
const SW_STEP_TIMEOUT_MS = 5_000;

/**
 * True when the deployed build is newer than this tab's. Any failure —
 * offline, a 404 from a deploy without the file, malformed JSON — reads as
 * "no update", so a flaky network never locks the user behind the prompt.
 */
export async function checkForUpdate(): Promise<boolean> {
  if (import.meta.env.DEV) return false;
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return false;
    const body = (await res.json()) as { version?: unknown };
    const remote = Number(body.version);
    const local = Number(__APP_VERSION__);
    return Number.isFinite(remote) && Number.isFinite(local) && remote > local;
  } catch {
    return false;
  }
}

function waitForState(worker: ServiceWorker, state: ServiceWorkerState): Promise<void> {
  return new Promise((resolve) => {
    if (worker.state === state) return resolve();
    const timer = setTimeout(resolve, SW_STEP_TIMEOUT_MS);
    worker.addEventListener('statechange', () => {
      if (worker.state === state || worker.state === 'redundant') {
        clearTimeout(timer);
        resolve();
      }
    });
  });
}

/**
 * Get the new service worker in charge, then reload.
 *
 * A plain reload is not enough for signed-in users. The worker answers
 * navigations with its precached index.html, and it is built without
 * skipWaiting/clientsClaim (injectRegister is false, so vite-plugin-pwa does
 * not add them). A newer worker therefore installs and then sits in
 * `waiting` until every tab closes, and a reload meanwhile gets the old
 * index.html back — the prompt would return straight after the refresh.
 *
 * So: fetch the latest worker, tell a waiting one to skip waiting (the
 * generated sw.js listens for SKIP_WAITING), wait for it to activate, and
 * only then reload, which the new worker answers with the new index.html.
 * Each step is time-boxed; the reload happens either way.
 */
export async function applyUpdate(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) {
      await reg.update().catch(() => {});
      if (reg.installing) await waitForState(reg.installing, 'installed');
      const waiting = reg.waiting;
      if (waiting) {
        waiting.postMessage({ type: 'SKIP_WAITING' });
        await waitForState(waiting, 'activated');
      }
    }
  } catch {
    // No service worker API, or the registration lookup failed. Nothing is
    // serving a stale shell in that case, so a plain reload is correct.
  }
  window.location.reload();
}
